import dot from 'dot-object';
import type { JsonDataItem } from '../generic-types';
import DataGridDataCell from './cells/data-grid-data-cell.component';
import DataGridDataDateCell from './cells/data-grid-data-date-cell.component';
import DataGridDataDropDownCell from './cells/data-grid-data-drop-down-cell.component';
import DataGridDataMultiSelectCell from './cells/data-grid-data-multiselect-cell.component';
import DataGridDataNumericalCell from './cells/data-grid-data-numerical-cell.component';
import DataGridDataTextCell from './cells/data-grid-data-text-cell.component';
import DataGridCustomCell from './data-grid-custom-cell.component';
import type { DataGridCellRenderer } from './data-grid-custom-column.interface';
import { DataGridColumnType } from './data-grid-enums';
import type { DataGridColumnSettings } from './data-grid-settings.interface';
import type { Ref } from 'react';
import { forwardRef } from 'react';

export interface DataGridCellSelectorProps {
  item: JsonDataItem;
  column: DataGridColumnSettings;
  isEditing?: boolean;
  components?: Record<string, DataGridCellRenderer>;
  allowCellScrollBar?: boolean;
}

function DataGridCellSelector(
  { item, column, isEditing = false, components, allowCellScrollBar }: DataGridCellSelectorProps,
  ref: Ref<HTMLDivElement>
) {
  const value = (column.valuePath ? dot.pick(column.valuePath, item) : item[column.field]) ?? '';
  switch (column.type) {
    case DataGridColumnType.Text:
      return <DataGridDataTextCell ref={ref} value={value} isEditing={isEditing} width={column.width} />;
    case DataGridColumnType.Number:
      return <DataGridDataNumericalCell ref={ref} value={value} isEditing={isEditing} width={column.width} />;
    case DataGridColumnType.Date:
      return (
        <DataGridDataDateCell ref={ref} value={value} isEditing={isEditing} dateOnly={true} width={column.width} />
      );
    case DataGridColumnType.Time:
      return (
        <DataGridDataDateCell ref={ref} value={value} isEditing={isEditing} timeOnly={true} width={column.width} />
      );
    case DataGridColumnType.DateTime:
      return <DataGridDataDateCell value={value} isEditing={isEditing} width={column.width} />;
    case DataGridColumnType.Select:
      return (
        <DataGridDataDropDownCell ref={ref} value={value} isEditing={isEditing} column={column} width={column.width} />
      );
    case DataGridColumnType.Boolean:
      return <DataGridDataCell ref={ref} value={value} width={column.width} />;
    case DataGridColumnType.MultiSelect:
      return (
        <DataGridDataMultiSelectCell
          ref={ref}
          value={value}
          isEditing={isEditing}
          column={column}
          width={column.width}
        />
      );
    case DataGridColumnType.Custom:
      return components && column.cellRenderer ? (
        <DataGridCustomCell ref={ref} data={item} column={column} components={components} />
      ) : (
        <DataGridDataCell ref={ref} value={value} width={column.width} allowCellScrollBar={allowCellScrollBar} />
      );
    default:
      return <DataGridDataCell ref={ref} value={value} width={column.width} />;
  }
}

export default forwardRef(DataGridCellSelector);
